import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowUpRight, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/language/LanguageContext';

export const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Replace 'YOUR_FORM_ID' with your actual Formspree ID
      const FORMSPREE_FORM_ID = 'xzzzzzzz'; 
      const response = await fetch(`https://formspree.io/f/mykjvjkb`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(t('contact.toast.success'), {
          description: t('contact.toast.successDesc'),
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error(t('contact.toast.error'), {
        description: t('contact.toast.errorDesc'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: t('contact.email'),
      value: 'contact@isifu.dev',
      href: 'mailto:contact@isifu.dev',
    },
    {
      icon: MapPin,
      label: t('contact.location'),
      value: t('contact.locationValue'),
      href: null,
    },
  ];

  const socialLinks = [
    { label: 'GitHub', href: '#' },
    { label: 'LinkedIn', href: '#' },
    { label: 'Twitter', href: '#' },
    { label: 'Dribbble', href: '#' },
  ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="min-h-[90dvh] bg-background py-24 lg:py-32 px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div
          className={`mb-16 lg:mb-24 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-muted-foreground text-sm font-light tracking-[0.2em] uppercase mb-4">
            {t('contact.sectionTitle')}
          </p>
          <div className="w-12 h-px bg-border" />
        </div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left column - CTA */}
          <div
            className={`transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2 className="text-foreground text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight mb-8">
              {t('contact.heading1')}
              <br />
              <span className="text-muted-foreground">{t('contact.heading2')}</span>
            </h2>

            <p className="text-muted-foreground font-light leading-relaxed mb-12 max-w-md">
              {t('contact.description')}
            </p>

            {/* Contact info */}
            <div className="space-y-6 mb-12">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 border border-border flex items-center justify-center">
                    <item.icon size={18} className="text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs font-light tracking-wide uppercase">
                      {item.label}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-foreground text-sm font-light hover:text-muted-foreground transition-colors"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-foreground text-sm font-light">
                        {item.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social links */}
            <div>
              <p className="text-muted-foreground text-xs font-light tracking-[0.2em] uppercase mb-4">
                {t('contact.followMe')}
              </p>
              <div className="flex flex-wrap gap-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="group flex items-center gap-1 text-foreground text-sm font-light hover:text-muted-foreground transition-colors"
                  >
                    {link.label}
                    <ArrowUpRight
                      size={14}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Form */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="text-muted-foreground text-xs font-light tracking-wide uppercase mb-2 block"
                >
                  {t('contact.form.name')}
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.form.namePlaceholder')}
                  required
                  className="bg-transparent border-border focus:border-foreground/50 rounded-none h-12 text-foreground placeholder:text-muted-foreground/50 font-light"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="text-muted-foreground text-xs font-light tracking-wide uppercase mb-2 block"
                >
                  {t('contact.form.email')}
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('contact.form.emailPlaceholder')}
                  required
                  className="bg-transparent border-border focus:border-foreground/50 rounded-none h-12 text-foreground placeholder:text-muted-foreground/50 font-light"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="text-muted-foreground text-xs font-light tracking-wide uppercase mb-2 block"
                >
                  {t('contact.form.message')}
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.form.messagePlaceholder')}
                  required
                  rows={6}
                  className="bg-transparent border-border focus:border-foreground/50 rounded-none text-foreground placeholder:text-muted-foreground/50 font-light resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-12 bg-foreground text-background hover:bg-foreground/90 rounded-none font-light tracking-wide transition-all"
              >
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
